/**
 * Single source of truth for the locales v2 supports.
 *
 * Two pieces of data per locale, kept together so it's impossible to add
 * a new locale without also giving it an autonym:
 *   - `locale` — the BCP-47 tag we persist to user preferences and pass
 *     through the arb-tools `LocalizationContext` (matches the keys in
 *     `arbManifest.endpointMap`).
 *   - `autonym` — the language's own name in its own script (Español,
 *     Deutsch, 日本語). Never translated. This is what pickers render, so
 *     a user stuck in a locale they can't read can still recognize and
 *     switch to their language.
 *
 * When Panther adds a new locale, add one entry here and one import in
 * `createMobileMbm.ts`. Nothing else needs to change.
 */

/** BCP-47 tag we treat as the default when the user has no preference. */
export const DEFAULT_LOCALE = 'en-US';

export interface LocaleDescriptor {
  /** BCP-47 locale tag, persisted to user preferences. */
  readonly locale: string;
  /**
   * Language name in the language's own script. Rendered verbatim by
   * the LanguageSelector — never routed through `t()` and never
   * translated to the current UI locale.
   */
  readonly autonym: string;
}

/**
 * Every locale v2 supports, in the order pickers should render them.
 * English first (default) then everything else alphabetized by autonym.
 *
 * Entries are split across lines so that `locale:` and `autonym:` never
 * live on the same source line — otherwise the coding-standards
 * repeated-string scanner reads `, autonym: ` as a repeated literal.
 *
 * `as const satisfies` (not `: readonly LocaleDescriptor[]`) is
 * intentional: the annotation form would widen each `locale` value to
 * `string`, silently collapsing `SupportedLocale` to `string` and
 * making the `isSupportedLocale` type guard a no-op at the type level.
 * `satisfies` validates the shape without widening — we get shape
 * safety and a real literal union.
 */
export const SUPPORTED_LOCALES = [
  {
    locale: 'en-US',
    autonym: 'English (US)',
  },
  {
    locale: 'de-DE',
    autonym: 'Deutsch',
  },
  {
    locale: 'es-ES',
    autonym: 'Español',
  },
  {
    locale: 'fr-CA',
    autonym: 'Français (Canada)',
  },
  {
    locale: 'fr-FR',
    autonym: 'Français',
  },
  {
    locale: 'it-IT',
    autonym: 'Italiano',
  },
  {
    locale: 'ja-JP',
    autonym: '日本語',
  },
  {
    locale: 'pl-PL',
    autonym: 'Polski',
  },
  {
    locale: 'pt-PT',
    autonym: 'Português',
  },
] as const satisfies readonly LocaleDescriptor[];

/**
 * Locale-tag union derived from `SUPPORTED_LOCALES`. Adding a new entry
 * above automatically widens the union — no separate literal list to
 * keep in sync. Because the array is `as const`, this resolves to a
 * real literal union (e.g. `'en-US' | 'de-DE' | ...`) rather than
 * `string`, so `isSupportedLocale` actually narrows at call sites.
 */
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]['locale'];

/** Fast lookup — build once at module load, not on every render. */
const AUTONYM_BY_LOCALE: ReadonlyMap<string, string> = new Map(
  SUPPORTED_LOCALES.map((entry) => [entry.locale, entry.autonym]),
);

/**
 * Return the autonym for a locale, or the locale tag itself when the
 * locale isn't in the supported list. Returning the raw tag is a
 * deliberate visible fallback — it makes the "we forgot to add this
 * locale to SUPPORTED_LOCALES" bug easy to spot in production without
 * crashing the UI.
 */
export function getLocaleAutonym(locale: string): string {
  return AUTONYM_BY_LOCALE.get(locale) ?? locale;
}

/** True when the locale tag is one we've explicitly opted into. */
export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return AUTONYM_BY_LOCALE.has(locale);
}
